// CareLogix™ ORACLE — OraclePredictiveChart (Phase 33 §2)
// Forecast-band visualization with probability cone (deterministic, animated).
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE, buildSparkline } from "@/components/animation/MotionShared";
import { predictiveApi } from "@/api/phase33Client";

function band(symbol, domain, points, base, spread) {
  const seed = `${symbol}-${domain}-band`;
  const mid  = buildSparkline({ seed, points, base, vol: 2.4 });
  const up   = mid.map((v, i) => v + (spread * (0.5 + i / points)));
  const dn   = mid.map((v, i) => v - (spread * (0.5 + i / points)));
  return { mid, up, dn };
}

function polyline(arr, w, h, lo, hi, pad = 6) {
  const stepX = (w - pad * 2) / Math.max(1, arr.length - 1);
  return arr.map((v, i) => {
    const x = pad + i * stepX;
    const y = h - pad - ((v - lo) / Math.max(0.001, hi - lo)) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
}

export default function OraclePredictiveChart({ symbol = "AAPL", domain = "stocks", height = 240, testid = "oracle-predictive-chart" }) {
  const [signal, setSignal] = useState(null);
  useEffect(() => {
    let cancelled = false;
    predictiveApi.signal(symbol, domain).then((r) => { if (!cancelled) setSignal(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [symbol, domain]);

  const base = signal?.entry_exit?.price_reference || 120;
  const conf = signal?.scores?.confidence_score || 55;
  const spread = Math.max(2, 18 - conf / 10);
  const { mid, up, dn } = useMemo(() => band(symbol, domain, 48, base, spread), [symbol, domain, base, spread]);
  const W = 720, H = height;
  const lo = Math.min(...dn) - 2;
  const hi = Math.max(...up) + 2;
  const midP = polyline(mid, W, H, lo, hi);
  const upP  = polyline(up,  W, H, lo, hi);
  const dnP  = polyline([...dn].reverse(), W, H, lo, hi);
  const probUp   = signal?.breakout_probability ?? 50;
  const probRev  = signal?.trend_reversal_probability ?? 40;
  const uncertainty = signal?.uncertainty_pct ?? Math.max(10, 100 - conf);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <div style={S.eyebrow}>ORACLE PREDICTIVE CHART™</div>
          <h3 style={S.title}>{symbol} Forecast Band</h3>
        </div>
        <div style={S.kpis}>
          <span style={{ ...S.pill, color: PALETTE.greenAccent, borderColor: PALETTE.greenAccent }} data-testid={`${testid}-breakout`}>BREAKOUT {probUp}%</span>
          <span style={{ ...S.pill, color: PALETTE.amber, borderColor: PALETTE.amber }} data-testid={`${testid}-reversal`}>REVERSAL {probRev}%</span>
          <span style={{ ...S.pill, color: PALETTE.slate4, borderColor: PALETTE.slate4 }} data-testid={`${testid}-uncertainty`}>UNCERTAINTY {uncertainty}%</span>
        </div>
      </header>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" data-testid={`${testid}-svg`}>
        <defs>
          <linearGradient id={`${testid}-cone`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor={PALETTE.greenAccent} stopOpacity="0.30" />
            <stop offset="100%" stopColor={PALETTE.greenAccent} stopOpacity="0.06" />
          </linearGradient>
        </defs>
        <motion.polygon points={`${upP} ${dnP}`} fill={`url(#${testid}-cone)`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9 }} />
        <motion.polyline points={midP} fill="none" stroke={PALETTE.goldBright} strokeWidth="2" strokeDasharray="4 3" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.4 }} />
      </svg>
      <p style={S.note} data-testid={`${testid}-note`}>
        Probability cone is a research-only visualization, not a guarantee. Uncertainty widens to the right as horizon increases.
      </p>
      <footer style={S.foot}>Compliance Lock™ · Research only · HITL required.</footer>
    </section>
  );
}

const S = {
  shell:    { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 16, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 10, minWidth: 0 },
  head:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" },
  eyebrow:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.20em" },
  title:    { margin: 0, color: PALETTE.goldBright, fontSize: 16 },
  kpis:     { display: "flex", gap: 6, flexWrap: "wrap" },
  pill:     { fontFamily: "'Space Mono', monospace", fontSize: 11, padding: "3px 9px", borderRadius: 999, border: "1px solid", letterSpacing: "0.06em" },
  note:     { color: PALETTE.slate4, fontSize: 12, margin: 0, lineHeight: 1.5 },
  foot:     { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, textAlign: "right" },
};
